import { getI18n } from '@/plugins/i18n'
import { formatTime } from '@/lib/utils'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useLookup, useCurrencyColumnFormatter, useCurrencyOptions } from '@/composables/data-grid'
import { useExpenseClaimColumn, useInvoiceColumn, useStaffColumn, useUnitOfMeasureColumn } from '@/composables/data-grid-column'

export const cloneCostReportRow = (row) => {
  const clone = { ...row }
  delete clone.id
  delete clone.invoice
  return clone
}

export const useLockedCostReportRow = () => {
  const i18n = getI18n()
  const { costReportRowLockEnum } = useAccountingStore()
  const lockMessages = {
    [costReportRowLockEnum.INVOICE]: 'info.costReportRowInvoicedLocked',
    [costReportRowLockEnum.EXPENSE_CLAIM]: 'info.costReportRowClaimedLocked',
  }

  const isLocked = (row) => row?.lock_reason != null
  const isInvoiced = (row) => row?.invoice != null
  const lockMessage = (row) => isLocked(row) ? i18n.t(lockMessages[row.lock_reason]) : null
  return { isLocked, isInvoiced, lockMessage }
}

export const useCostReportRowLabels = () => {
  const accountingStore = useAccountingStore()
  const planningStore = usePlanningStore()
  const { costReportRowTypeEnum } = accountingStore

  const getStaffName = (staffId) => planningStore.staff.find(s => s.id === staffId)?.surname_name || '-'
  const getTagName = (tagId) => accountingStore.costReportRowTagById.get(tagId)?.name || null
  const getItemLabel = (row) => {
    if (row.type === costReportRowTypeEnum.EXPENSE) return getTagName(row.tag) || row.notes || getStaffName(row.staff)
    return getStaffName(row.staff)
  }

  return { getStaffName, getItemLabel }
}

export const useCostReportRowTypes = () => {
  const i18n = getI18n()
  const { costReportRowTypeEnum } = useAccountingStore()

  const types = [
    { id: costReportRowTypeEnum.TIME, name: i18n.t('common.costReportTime'), color: '#1976D2' },
    { id: costReportRowTypeEnum.EXPENSE, name: i18n.t('common.expense'), color: '#2E7D32' },
    { id: costReportRowTypeEnum.COMMENT, name: i18n.t('common.comment'), color: '#6D4C41' },
  ]

  return { types, getTypeChips: (type) => types.filter(item => item.id === type) }
}

export const formatMinutes = (mins) => {
  if (mins == null) return ''
  return useAccountingStore().companyData.time_display_in_hours
    ? +(mins / 60).toFixed(2) + ' h'
    : mins + ' min'
}

export const useCostReportRowColumns = (currencyId) => {
  const i18n = getI18n()
  const accountingStore = useAccountingStore()
  const webSiteStore = useWebSiteStore()
  const { types } = useCostReportRowTypes()

  const timeColumn = (dataField, caption) => ({
    dataField,
    caption: i18n.t(caption),
    dataType: 'string',
    calculateDisplayValue: (row) => formatTime(row[dataField]),
    width: '7rem',
    visible: false,
  })

  const minutesColumn = (dataField, caption) => ({
    dataField,
    caption: i18n.t(caption),
    dataType: 'number',
    calculateDisplayValue: (row) => formatMinutes(row[dataField]),
    width: '7rem',
  })

  return [
    { dataField: 'date', caption: i18n.t('common.date'), dataType: 'date', sortOrder: 'asc' },
    {
      dataField: 'type',
      caption: i18n.t('common.type'),
      cellTemplate: 'type-cell-template',
      width: '6rem',
      lookup: useLookup({ dataSource: types, valueExpr: 'id', displayExpr: 'name' }),
    },
    useStaffColumn(),
    timeColumn('work_start', 'common.workStart'),
    timeColumn('work_end', 'common.workEnd'),
    minutesColumn('work_minutes', 'common.totalWorkedHours'),
    timeColumn('reported_start', 'common.reportedStart'),
    timeColumn('reported_end', 'common.reportedEnd'),
    minutesColumn('reported_minutes', 'common.totalReportedHours'),
    timeColumn('travel_start', 'common.travelTimeStart'),
    timeColumn('travel_end', 'common.travelTimeEnd'),
    minutesColumn('travel_minutes', 'common.totalTravelMinutes'),
    timeColumn('pause_start', 'common.pauseStart'),
    timeColumn('pause_end', 'common.pauseEnd'),
    minutesColumn('pause_minutes', 'common.totalPauseMinutes'),
    {
      dataField: 'tag',
      caption: i18n.t('common.tag'),
      width: '12rem',
      lookup: useLookup({ dataSource: accountingStore.getLocalizedCostReportRowTags(), valueExpr: 'id', displayExpr: 'name' }),
    },
    useUnitOfMeasureColumn(),
    { dataField: 'quantity', caption: i18n.t('common.quantityAlt'), dataType: 'number', format: { type: 'fixedPoint', precision: 2 } },
    {
      ...useCurrencyOptions({ currency: webSiteStore.currencies.find(c => c.id === currencyId)?.code }),
      dataField: 'cost',
      caption: i18n.t('common.cost'),
      dataType: 'number',
      calculateDisplayValue: (rowData) => rowData.cost != null ? useCurrencyColumnFormatter(rowData, 'cost', rowData.currency_ref ?? currencyId) : '',
    },
    {
      dataField: 'currency_ref',
      caption: i18n.t('common.currency'),
      lookup: useLookup({ dataSource: webSiteStore.getLocalizedCurrencies(), valueExpr: 'id', displayExpr: 'name' }),
    },
    { dataField: 'is_rebillable', caption: i18n.t('common.rebillable'), dataType: 'boolean', visible: false },
    { dataField: 'notes', caption: i18n.t('common.notes') },
    useInvoiceColumn(),
    useExpenseClaimColumn(),
  ]
}
